import { useState, useEffect, useRef } from 'react'
import { syncApi } from '../services/api'
import Button from '../components/common/Button'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'

export default function SyncDashboard() {
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState(null)
    const [pendingTimesheets, setPendingTimesheets] = useState([])
    const [selectedTimesheets, setSelectedTimesheets] = useState([])
    const [selectedJob, setSelectedJob] = useState(null)
    const [unsubmittedTimesheets, setUnsubmittedTimesheets] = useState([])
    const [loadingJob, setLoadingJob] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [waitingForResult, setWaitingForResult] = useState(null)
    const waitingForResultRef = useRef(null)
    const [expandedLog, setExpandedLog] = useState(null)
    const pollIntervalRef = useRef(null)
    const navigate = useNavigate()

    useEffect(() => {
        loadStatus()
        loadPendingTimesheets()

        // Refresh status every 30 seconds
        const interval = setInterval(loadStatus, 30000)
        return () => {
            clearInterval(interval)
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
        }
    }, [])

    const openInspections = async () => {
        try {
            const data = await api.post('/api/auth/inspection-bypass');
            if (data.success) {
                window.open(data.url, '_blank');
            }
        } catch (error) {
            console.error('Failed to generate bypass token:', error);
        }
    };

    async function loadStatus() {
        try {
            const response = await syncApi.getStatus()
            if (response.success) {
                setStatus(response.data)

                // Check if we're waiting for a result and it's now in the logs
                const waiting = waitingForResultRef.current
                if (waiting && response.data?.recentLogs?.length > 0) {
                    const latestLog = response.data.recentLogs[0]
                    const logTime = new Date(latestLog.createdAt).getTime()
                    const waitStartTime = waiting.startTime

                    // If latest log is after we started waiting and matches the command type
                    if (logTime > waitStartTime &&
                        latestLog.commandType === waiting.type &&
                        (latestLog.status === 'success' || latestLog.status === 'failed' || latestLog.status === 'completed')) {
                        // Result arrived - stop polling
                        setWaitingForResult(null)
                        waitingForResultRef.current = null
                        if (pollIntervalRef.current) {
                            clearInterval(pollIntervalRef.current)
                            pollIntervalRef.current = null
                        }

                        // Show toast with result
                        if (latestLog.status === 'success' || latestLog.status === 'completed') {
                            toast.success(latestLog.message || `${latestLog.commandType.replace('_', ' ')} completed`)
                        } else {
                            toast.error(latestLog.message || `${latestLog.commandType.replace('_', ' ')} failed`)
                        }

                        // Refresh pending timesheets if it was an export
                        if (waiting.type === 'export_timesheets') {
                            loadPendingTimesheets()
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load status:', error)
        }
        setLoading(false)
    }

    async function loadPendingTimesheets() {
        try {
            const response = await syncApi.getPendingTimesheets()
            if (response.success) {
                setPendingTimesheets(response.timesheets || [])
            }
        } catch (error) {
            console.error('Failed to load pending timesheets:', error)
        }
    }

    function startPolling(commandType) {
        // Clear any existing poll interval
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
        }

        // Set waiting state (both state and ref)
        const waitData = { type: commandType, startTime: Date.now() }
        setWaitingForResult(waitData)
        waitingForResultRef.current = waitData

        // Poll every 2 seconds for results
        pollIntervalRef.current = setInterval(() => {
            loadStatus()
        }, 2000)

        // Stop polling after 2 minutes max
        setTimeout(() => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current)
                pollIntervalRef.current = null
                setWaitingForResult(null)
                waitingForResultRef.current = null
            }
        }, 120000)
    }

    async function handleSync(type) {
        setSyncing(true)
        try {
            const response = await syncApi.trigger(type)
            if (response.success) {
                toast.loading(`${type.replace('_', ' ')} in progress...`, { duration: 3000 })
                startPolling(type)
                loadStatus()
            } else {
                toast.error(response.message || 'Sync failed')
            }
        } catch (error) {
            toast.error('Error triggering sync')
        }
        setSyncing(false)
    }

    async function handleExport() {
        if (selectedTimesheets.length === 0) {
            toast.error('Please select timesheets to export')
            return
        }

        setSyncing(true)
        toast.loading(`Generating ${selectedTimesheets.length} PDF(s)...`, { id: 'export' })

        try {
            for (const uuid of selectedTimesheets) {
                // Trigger download for each selected timesheet
                const response = await fetch(`/api/timesheets/${uuid}/download-pdf`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                })

                if (response.ok) {
                    const blob = await response.blob()
                    const contentDisposition = response.headers.get('Content-Disposition')
                    let filename = 'timesheet.pdf'
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^"]+)"?/)
                        if (match) filename = match[1]
                    }

                    // Create download link
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = filename
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    a.remove()
                } else {
                    toast.error(`Failed to download PDF for one timesheet`)
                }
            }

            toast.success(`Downloaded ${selectedTimesheets.length} PDF(s)`, { id: 'export' })
            setSelectedTimesheets([])
            setSelectedJob(null)
        } catch (error) {
            toast.error('Error generating PDFs', { id: 'export' })
            console.error(error)
        }
        setSyncing(false)
    }

    // Group timesheets by customer
    const groupedJobs = pendingTimesheets.reduce((acc, ts) => {
        const key = `${ts.customer}`
        if (!acc[key]) {
            acc[key] = {
                customer: ts.customer,
                timesheets: []
            }
        }
        acc[key].timesheets.push(ts)
        return acc
    }, {})

    const jobs = Object.values(groupedJobs).sort((a, b) =>
        a.customer.localeCompare(b.customer)
    )

    const selectedJobData = selectedJob !== null ? jobs[selectedJob] : null

    function toggleTimesheet(uuid) {
        setSelectedTimesheets(prev =>
            prev.includes(uuid)
                ? prev.filter(id => id !== uuid)
                : [...prev, uuid]
        )
    }

    function selectAllInJob() {
        if (!selectedJobData) return
        const jobUuids = selectedJobData.timesheets.map(ts => ts.uuid)
        const allSelected = jobUuids.every(uuid => selectedTimesheets.includes(uuid))

        if (allSelected) {
            setSelectedTimesheets(prev => prev.filter(uuid => !jobUuids.includes(uuid)))
        } else {
            setSelectedTimesheets(prev => [...new Set([...prev, ...jobUuids])])
        }
    }

    async function handleSelectJob(index) {
        setSelectedJob(index)
        setUnsubmittedTimesheets([])
        const job = jobs[index]
        if (!job) return

        setLoadingJob(true)
        try {
            const response = await syncApi.getJobTimesheets(job.workOrderNumber)
            if (response.success && response.data?.unsubmitted) {
                setUnsubmittedTimesheets(response.data.unsubmitted)
            }
        } catch (error) {
            console.error('Failed to load job timesheets:', error)
        }
        setLoadingJob(false)
    }

    function getTypeBadge(type) {
        const types = {
            '0': { label: 'Timesheet', class: 'bg-blue-100 text-blue-800' },
            '1': { label: 'Repair', class: 'bg-purple-100 text-purple-800' },
            '2': { label: 'Packing', class: 'bg-green-100 text-green-800' },
            '3': { label: 'Internal', class: 'bg-gray-100 text-gray-800' },
            '4': { label: 'Pickup', class: 'bg-yellow-100 text-yellow-800' },
            '5': { label: 'Vendor', class: 'bg-orange-100 text-orange-800' },
        }
        const t = types[type] || { label: 'Unknown', class: 'bg-gray-100' }
        return <span className={`text-xs px-2 py-0.5 rounded ${t.class}`}>{t.label}</span>
    }

    function isErrorStatus(status) {
        return status === 'failed' || status === 'error'
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <h1 className="page-title">Sync Dashboard</h1>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={openInspections}>
                        📋 Inspections
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/company-settings')}>
                        🏢 Company Info
                    </Button>
                    <Button variant="secondary" onClick={loadStatus}>
                        🔄 Refresh
                    </Button>
                </div>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">Agent Status</p>
                    <p className={`text-2xl font-bold ${status?.isOnline ? 'text-green-500' : 'text-red-500'}`}>
                        {status?.isOnline ? 'Online' : 'Offline'}
                    </p>
                    {status?.lastHeartbeat && (
                        <p className="text-xs text-gray-400 mt-1">
                            Last seen: {new Date(status.lastHeartbeat).toLocaleTimeString()}
                        </p>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">QuickBooks</p>
                    <p className={`text-2xl font-bold ${status?.quickBooksConnected ? 'text-green-500' : 'text-red-500'}`}>
                        {status?.quickBooksConnected ? 'Connected' : 'Disconnected'}
                    </p>
                    {status?.agentVersion && (
                        <p className="text-xs text-gray-400 mt-1">v{status.agentVersion}</p>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">Pending Export</p>
                    <p className="text-2xl font-bold text-rdi-blue">
                        {pendingTimesheets.length}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Timesheets ready</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">Queue</p>
                    <p className="text-2xl font-bold text-rdi-blue">
                        {status?.pendingCommands || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Commands pending</p>
                </div>
            </div>

            {/* Sync Actions */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Sync Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Button
                        onClick={() => handleSync('sync_customers')}
                        loading={syncing || waitingForResult?.type === 'sync_customers'}
                    >
                        {waitingForResult?.type === 'sync_customers' ? 'Syncing...' : 'Sync Jobs & Customers'}
                    </Button>
                    <Button
                        onClick={() => handleSync('sync_inventory')}
                        loading={syncing || waitingForResult?.type === 'sync_inventory'}
                    >
                        {waitingForResult?.type === 'sync_inventory' ? 'Syncing...' : 'Sync Inventory'}
                    </Button>
                    <Button variant="secondary" onClick={loadStatus}>
                        Refresh Status
                    </Button>
                </div>
                {waitingForResult && (
                    <p className="text-sm text-blue-600 mt-3 animate-pulse">
                        ⏳ Waiting for {waitingForResult.type.replace('_', ' ')} to complete...
                    </p>
                )}
            </div>

            {/* Two Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Jobs Panel */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="bg-rdi-blue text-white px-4 py-3 flex justify-between items-center">
                        <span className="font-semibold">Pending Jobs Ready</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded text-sm">{jobs.length}</span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {jobs.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">No pending jobs</div>
                        ) : (
                            <ul>
                                {jobs.map((job, index) => (
                                    <li
                                        key={`${job.customer}`}
                                        onClick={() => handleSelectJob(index)}
                                        className={`
                                            p-4 border-b cursor-pointer transition-colors
                                            ${selectedJob === index ? 'bg-blue-50 border-l-4 border-l-rdi-blue' : 'hover:bg-gray-50'}
                                        `}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-medium text-gray-800">{job.customer}</p>
                                                <p className="text-sm text-gray-500">{job.timesheets.length} timesheet{job.timesheets.length !== 1 ? 's' : ''}</p>
                                            </div>
                                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-semibold">
                                                {job.timesheets.length}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Timesheets Panel */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="bg-rdi-blue text-white px-4 py-3 flex justify-between items-center">
                        <span className="font-semibold">Pending Timesheets for Export</span>
                        <div className="flex items-center gap-2">
                            {unsubmittedTimesheets.length > 0 && (
                                <span className="bg-amber-400 text-amber-900 px-2 py-0.5 rounded text-xs font-semibold">
                                    {unsubmittedTimesheets.length} unsubmitted
                                </span>
                            )}
                            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
                                {selectedJobData?.timesheets.length || 0}
                            </span>
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {!selectedJobData ? (
                            <div className="text-center py-12 text-gray-500">
                                <p className="text-2xl mb-2">←</p>
                                <p>Select a job to view timesheets</p>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 bg-gray-50 border-b flex items-center">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedJobData.timesheets.every(ts =>
                                                selectedTimesheets.includes(ts.uuid)
                                            )}
                                            onChange={selectAllInJob}
                                            className="w-4 h-4 mr-2"
                                        />
                                        <span className="font-medium">Select All</span>
                                    </label>
                                </div>
                                {unsubmittedTimesheets.length > 0 && (
                                    <div className="p-3 bg-amber-50 border-b border-amber-200">
                                        <div className="flex items-start gap-2">
                                            <span className="text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
                                            <div>
                                                <p className="text-sm font-semibold text-amber-800">
                                                    {unsubmittedTimesheets.length} Unsubmitted Timesheet{unsubmittedTimesheets.length !== 1 ? 's' : ''}
                                                </p>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    The following technicians have timesheets not yet submitted for this job:
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {[...new Set(unsubmittedTimesheets.map(ts => ts.technicianName))].map(name => (
                                                        <span key={name} className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                                                            {name}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className="mt-2 space-y-0.5">
                                                    {unsubmittedTimesheets.map(ts => (
                                                        <p key={ts.uuid} className="text-xs text-amber-600">
                                                            {ts.referenceNo} — {ts.technicianName} — {ts.month}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <ul>
                                    {selectedJobData.timesheets.map(ts => (
                                        <li key={ts.uuid} className="p-4 border-b hover:bg-gray-50">
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTimesheets.includes(ts.uuid)}
                                                    onChange={() => toggleTimesheet(ts.uuid)}
                                                    className="w-4 h-4 mr-3"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{ts.referenceNo}</span>
                                                        {getTypeBadge(ts.tsType)}
                                                    </div>
                                                    <p className="text-sm text-gray-500">{ts.employeeName} — {ts.month}</p>
                                                </div>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Export Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <p className="text-gray-600">
                        <span className="font-bold text-rdi-blue">{selectedTimesheets.length}</span> timesheet(s) selected
                    </p>
                    <button
                        onClick={() => navigate('/admin/rollback')}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                        Rollback Exported
                    </button>
                </div>
                <Button
                    onClick={handleExport}
                    loading={syncing || waitingForResult?.type === 'export_timesheets'}
                    disabled={selectedTimesheets.length === 0}
                >
                    {syncing ? 'Generating PDFs...' : 'Download Selected PDFs'}
                </Button>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="px-4 py-3 border-b flex justify-between items-center">
                    <h2 className="font-semibold text-gray-800">Recent Sync Activity</h2>
                    {waitingForResult && (
                        <span className="text-xs text-blue-600 animate-pulse">Auto-refreshing...</span>
                    )}
                </div>
                {(!status?.recentLogs || status.recentLogs.length === 0) ? (
                    <div className="text-center py-8 text-gray-500">
                        No recent activity
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Time</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Action</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Records</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Message</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {status.recentLogs.map((log, index) => (
                                    <tr
                                        key={index}
                                        className={isErrorStatus(log.status) ? 'bg-red-50' : ''}
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                            {new Date(log.createdAt).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium">
                                            {log.commandType?.replace(/_/g, ' ')}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-1 rounded font-medium ${log.status === 'success' || log.status === 'completed'
                                                ? 'bg-green-100 text-green-800'
                                                : isErrorStatus(log.status)
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{log.recordsAffected}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {log.message && log.message.length > 60 ? (
                                                <div>
                                                    <span className={isErrorStatus(log.status) ? 'text-red-700' : 'text-gray-600'}>
                                                        {expandedLog === index
                                                            ? log.message
                                                            : `${log.message.substring(0, 60)}...`}
                                                    </span>
                                                    <button
                                                        onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                                                        className="ml-2 text-blue-600 hover:underline text-xs"
                                                    >
                                                        {expandedLog === index ? 'Show less' : 'Show more'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className={isErrorStatus(log.status) ? 'text-red-700 font-medium' : 'text-gray-600'}>
                                                    {log.message || '-'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}